import { ChangeEvent, JSX, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import FeedbackButton from "lib/components/button/FeedbackButton";

import { asTitle } from "utils/Utils";
import useFetch from "utils/fetch/useFetch";
import useGet from "utils/fetch/useGet";

import { getLoggedInUser } from "authentication/AuthenticationService";

import BlueprintClient from "core/blueprint/base/BlueprintClient";
import {
	BlueprintType,
	Right,
	UserRights
} from "core/blueprint/base/BlueprintService";
import { Import } from "core/blueprint/base/BlueprintTypes";
import UserRightsTable from "core/blueprint/base/access/UserAccessTable";
import LibraryClient from "core/library/LibraryClient";
import { LibraryBlueprint } from "core/library/LibraryTypes";

export type ImportBlueprintDialogProps = {
	onClose: () => void;
	onConfirm: (id: number, type: BlueprintType) => void;
};

export default function ImportBlueprintDialog(
	props: Readonly<ImportBlueprintDialogProps>
): JSX.Element {
	const { t } = useTranslation();
	const { response } = useGet(LibraryClient.getAll());
	const { call } = useFetch();

	const blueprints: LibraryBlueprint[] = useMemo(() => {
		if (response.state !== "SUCCESS") {
			return [];
		}

		return response.data;
	}, [response]);

	const [id, setId] = useState<number>();
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [userRights, setUserRights] = useState<UserRights[]>([
		{
			user: getLoggedInUser(),
			rights: [Right.ADMIN, Right.WRITE, Right.READ]
		}
	]);
	const [accessesValid, setAccessesValid] = useState(true);

	function nameIsInvalid() {
		return name.trim().length === 0;
	}

	function isValid() {
		return id !== undefined && !nameIsInvalid() && accessesValid;
	}

	function importBlueprint(): Promise<void> {
		const blueprint = blueprints.find(bp => bp.id === id);
		if (id === undefined || blueprint === undefined) {
			return Promise.resolve();
		}

		const request: Import = {
			id,
			name,
			description,
			userRights
		};

		call(BlueprintClient.importFromLibrary(request)).then(createdId =>
			props.onConfirm(createdId, blueprint.type)
		);

		return Promise.resolve();
	}

	const handleSelectBlueprint = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const id = Number(e.target.value);
			const newBlueprint = blueprints.find(b => b.id === id);
			if (newBlueprint === undefined) {
				return;
			}

			setId(id);

			if (name.trim() === "") {
				setName(newBlueprint.name);
			}

			if (description.trim() === "") {
				setDescription(newBlueprint.description);
			}
		},
		[blueprints, name, description]
	);

	return (
		<Dialog
			open={true}
			onClick={e => e.stopPropagation()}
			onClose={props.onClose}>
			<DialogTitle>{asTitle(t("importBlueprint"))}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							label={asTitle(t("blueprint"))}
							select
							required
							fullWidth
							value={id ?? ""}
							onChange={handleSelectBlueprint}
							variant="standard">
							{blueprints.map(blueprint => (
								<MenuItem
									key={blueprint.id}
									value={blueprint.id}>
									{t(blueprint.type.toLowerCase())}
									{" - "}
									{blueprint.name}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12}>
						<TextField
							type="text"
							label={asTitle(t("name"))}
							error={nameIsInvalid()}
							required
							fullWidth
							inputProps={{ maxLength: 255 }}
							helperText={name.length.toString() + "/255"}
							value={name}
							onChange={e => setName(e.target.value)}
							variant="standard"
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							type="text"
							label={asTitle(t("description"))}
							fullWidth
							multiline
							rows={3}
							inputProps={{ maxLength: 1000 }}
							helperText={description.length.toString() + "/1000"}
							value={description}
							onChange={e => setDescription(e.target.value)}
							variant="standard"
						/>
					</Grid>
					<Grid item xs={12}>
						<InputLabel id="access">
							{asTitle(t("access"))}
						</InputLabel>
						<UserRightsTable
							accesses={userRights}
							onChange={setUserRights}
							isValid={setAccessesValid}
						/>
					</Grid>
				</Grid>
				<DialogActions>
					<Button variant="outlined" onClick={props.onClose}>
						{asTitle(t("cancel"))}
					</Button>
					<FeedbackButton
						variant="contained"
						label={asTitle(t("import"))}
						disabled={!isValid()}
						onClick={importBlueprint}
					/>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
}
